/**
 * 分组下拉的显示（W5，设计 §3.5 末行、§8.3）：「Zone 1 › WIR · Windsor Island」。
 *
 * `/pms/groups/lookup` 回的行加上：
 * | 字段 | 例 | 用途 |
 * |---|---|---|
 * | `label` | `WIR · Windsor Island` | 代码 + 名字（名字按当前语言取 labelI18n；和代码相同时只显示代码） |
 * | `pathLabel` | `Zone 1 › WIR · Windsor Island` | 下拉 / 筛选项显示这个 |
 * | `depth` | `1` | 需要缩进的地方用 |
 *
 * `name` 保持后端原值（英文源）：有调用方按 `name` 和 `listing.group.name` 对值，不能改成译文。
 * 行按树的顺序（深度优先，同级按代码）排好。
 */
// 这个文件被 src/hooks/useLookup.ts 引用：不 import 别的模块（apiBarrelGraph.test 限制了 useLookup 的 import 图规模），
// 所以树的走法在这里自己写一份最小的。

/** 分组名字的显示：当前语言的译文 → 英文译文 → `name`（同 pickI18n 的回退顺序）。 */
export function groupNameIn(g: { name: string; labelI18n?: Record<string, string> | null }, locale = 'en'): string {
  const map = g.labelI18n ?? {}
  return map[locale] || map.en || g.name
}

/** id 是 Snowflake ID 字符串（不 import SnowflakeId 类型，理由同上）。 */
export interface GroupLookupRow {
  id: string
  code: string
  name: string
  labelI18n?: Record<string, string> | null
  parentId: string | null
  typeCode?: string
  entityTypeCode?: string | null
  isActive?: boolean
  label: string
  pathLabel: string
  depth: number
}

export function decorateGroupLookup(raw: any[] | null | undefined, locale = 'en'): GroupLookupRow[] {
  const rows = (raw ?? []).map((r: any) => ({
    ...r,
    id: String(r.id),
    code: r.code ?? r.name ?? '',
    name: r.name ?? r.code ?? '',
    parentId: r.parentId != null ? String(r.parentId) : null,
    typeCode: r.typeCode ?? 'resort',
    isActive: r.isActive !== false,
  }))
  const ids = new Set(rows.map((r) => r.id))
  const children = new Map<string | null, typeof rows>()
  for (const r of rows) {
    const key = r.parentId && ids.has(r.parentId) ? r.parentId : null
    children.set(key, [...(children.get(key) ?? []), r])
  }
  for (const list of children.values()) list.sort((a, b) => a.code.localeCompare(b.code))
  const out: GroupLookupRow[] = []
  const seen = new Set<string>()
  const walk = (list: typeof rows, depth: number, path: string[]) => {
    for (const g of list) {
      if (seen.has(g.id)) continue
      seen.add(g.id)
      const full = groupNameIn(g, locale)
      const label = full && full !== g.code ? `${g.code} · ${full}` : g.code
      out.push({ ...g, label, pathLabel: [...path, label].join(' › '), depth })
      walk(children.get(g.id) ?? [], depth + 1, [...path, g.code])
    }
  }
  walk(children.get(null) ?? [], 0, [])
  return out
}

/**
 * 能挂房源的分组（`entityTypeCode = listing`，即 resort）。
 * 现在所有用分组的地方都按「房源直接挂的分组」精确匹配，选 Zone 选不到任何房源；
 * 后端阶段 3b 改成含下级之后，再按使用方逐个放开容器。旧数据没有 entityTypeCode 的行照旧可选。
 */
export function listingGroups(rows: GroupLookupRow[]): GroupLookupRow[] {
  return rows.filter((g) => g.entityTypeCode == null ? g.typeCode === 'resort' || !g.typeCode : g.entityTypeCode === 'listing')
}

/** 这个分组和它在 `rows` 里的全部下级（按 parentId 往下走）。`id` 不在 `rows` 里时只回它自己。 */
export function groupWithDescendants(rows: Pick<GroupLookupRow, 'id' | 'parentId'>[], id: string): string[] {
  const children = new Map<string, string[]>()
  for (const r of rows) {
    if (r.parentId) children.set(r.parentId, [...(children.get(r.parentId) ?? []), r.id])
  }
  const out: string[] = []
  const seen = new Set<string>()
  const stack = [id]
  while (stack.length) {
    const current = stack.pop() as string
    if (seen.has(current)) continue
    seen.add(current)
    out.push(current)
    stack.push(...(children.get(current) ?? []))
  }
  return out
}
